import "./Yearly.css"
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import APIURL from "../../common/Config";
import Header from "../header/header";
import Footer from "../footer/footer";

const AquariusCard = () => {
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inMemoryCacheRef = useRef(new Map());
  const [activeIndex, setActiveIndex] = useState(null);

  // Static metadata for AQUARIUS
  const staticMeta = {
    title: "AstroBhagya – Free Aquarius Yearly Horoscope by Best Astrologer",
    description: "Looking for accurate Aquarius yearly horoscope predictions 2026? Discover free Aquarius astrology predictions and annual insights by zodiac sign for a better year ahead.",
    keywords: "Free Aquarius yearly horoscope predictions 2026, Aquarius yearly Astrology predictions, Aquarius annual predictions, Yearly horoscope by Aquarius zodiac sign",
    author: "Heavenly Expectation Private Limited",
  };

  const toggleAnswer = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const fetchAquariusYearly = useCallback(async (retries = 3) => {
    const dateStr = "20260101"; // 2026 data
    const cacheKey = `aquarius_yearly_${dateStr}`; // 🔥 AQUARIUS cache key
    setError(null);
    setLoading(true);

    // 1. CHECK LOCALSTORAGE
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setHoroscopeData(parsed);
        setLoading(false);
        console.log("Aquarius Yearly data loaded from localStorage");
        return;
      } catch (e) {
        console.error("Failed to parse cached data:", e);
      }
    }

    // 2. CHECK IN-MEMORY CACHE
    if (inMemoryCacheRef.current.has(cacheKey)) {
      setHoroscopeData(inMemoryCacheRef.current.get(cacheKey));
      setLoading(false);
      console.log("Aquarius Yearly data loaded from memory cache");
      return;
    }

    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn('Fetch request timed out after 15 seconds.');
      }, 15000);

      const response = await fetch(
        `${APIURL}/horroscope/getallprediction?type=yearly&startDate=${dateStr}&endDate=${dateStr}&language=english`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        // Find AQUARIUS data (id: 40 or sunShine contains "AQUARIUS") 🔥 ID=40
        const aquariusData = data.find(
          item => item.id === 40 || item.sunShine?.toUpperCase().includes("AQUARIUS")
        );

        if (aquariusData) {
          // Cache the data
          inMemoryCacheRef.current.set(cacheKey, aquariusData);
          localStorage.setItem(cacheKey, JSON.stringify(aquariusData));
          setHoroscopeData(aquariusData);
          console.log("Aquarius Yearly data fetched successfully from API");
        } else {
          throw new Error("Aquarius data not found in API response");
        }
      } else {
        throw new Error("Unexpected response format from API");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('The network request timed out. Please check your internet connection and try again.');
        console.error("Network timeout error:", error);
      } else {
        setError(`Failed to load yearly horoscope: ${error.message}. Please try again.`);
        console.error("Error fetching Aquarius yearly data:", error);
      }

      if (retries > 0) {
        console.log(`Retrying fetch for Aquarius Yearly. Retries left: ${retries - 1}`);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return fetchAquariusYearly(retries - 1);
      } else {
        // FALLBACK DATA - AQUARIUS 2026 🔥
        console.log("All retries exhausted. Using fallback data.");
        const fallbackData = {
          sunShine: "Aquarius 2026: Your year, as the stars align",
          prediction: `<div class="horoscope">
            <p>2026 encourages Aquarius individuality and meaningful growth. Innovation meets practical implementation for success.</p>
            <h2>Love and Relationships</h2>
            <p>Relationships grow through honest communication. Transformative connections built on trust.</p>
            <h2>Career and Finances</h2>
            <p>Innovation drives career opportunities. Disciplined financial management ensures security.</p>
            <h2>Health & Wellness</h2>
            <p>Holistic approach maintains energy. Yoga and mindfulness support mental clarity.</p>
            <h2>Astrological Remedies</h2>
            <ul>
              <li><strong>Amethyst/Blue Sapphire:</strong> Enhances mental clarity and innovation.</li>
              <li><strong>Structured Routine:</strong> Balances creativity with stability.</li>
              <li><strong>Thursday Charity:</strong> Community service attracts positive energy.</li>
            </ul>
          </div>`
        };
        setHoroscopeData(fallbackData);
      }
    } finally {
      await minLoadingTime;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAquariusYearly();
  }, [fetchAquariusYearly]);

  // Set meta tags
  useEffect(() => {
    document.title = staticMeta.title;

    const setOrCreateMetaTag = (name, content) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (metaTag) {
        metaTag.setAttribute("content", content);
      } else {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", name);
        metaTag.setAttribute("content", content);
        document.head.appendChild(metaTag);
      }
    };

    setOrCreateMetaTag("description", staticMeta.description);
    setOrCreateMetaTag("keywords", staticMeta.keywords);
    setOrCreateMetaTag("author", staticMeta.author);
    setOrCreateMetaTag("revisit-after", "7 days");
    setOrCreateMetaTag("distribution", "global");
    setOrCreateMetaTag("rating", "general");

    let charsetMeta = document.querySelector("meta[charset]");
    if (!charsetMeta) {
      charsetMeta = document.createElement("meta");
      charsetMeta.setAttribute("charset", "UTF-8");
      document.head.prepend(charsetMeta);
    }
  }, []);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner" />
        <p>Loading Aquarius 2026 Horoscope...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button className="retry-button" onClick={() => fetchAquariusYearly()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div>
        <section id="Make">
          <div className="year-container">
            <h4 className="mt-3 mb-3 text-center font-weight-bold">
              {horoscopeData?.sunShine || "Aquarius 2026: Your year, as the stars align"}
            </h4>
            <div className="icons-wrapper text-center mb-4">
              <img
                src={require("../../assets/Aqauriusss.webp")}
                alt="Aquarius"
                className="horoscope-icon mx-2"
              />
            </div>

            {horoscopeData?.prediction && (
              <main>
                <div
                  dangerouslySetInnerHTML={{
                    __html: horoscopeData.prediction
                  }}
                />
              </main>
            )}

            <div className="btn-wrapper">
              <Link to="/yearly-horoscope/capricorn" className="btn btn-customs">
                <i className="bi bi-arrow-left"></i> Capricorn Horoscope 2026
              </Link>
              <Link to="/yearly-horoscope/pisces" className="btn btn-customs">
                <i className="bi bi-arrow-right"></i> Pisces Horoscope 2026
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ SECTION - SAME AS PREVIOUS */}
        <div className="faq-section">
          <div className="container-item">
            <h3>Frequently Asked Questions (FAQs) – Aquarius Yearly Horoscope</h3>
            <div className="fitem">
              {[
                {
                  question: "1. What are the core personality traits of Aquarius?",
                  answer: "Aquarius individuals are known for their innovation, independence, humanitarian spirit, and intellectual curiosity. Ruled by Uranus/Saturn, they excel in progressive thinking.",
                },
                {
                  question: "2. What can Aquarius expect in love and relationships in 2026?",
                  answer: "2026 brings growth through honest communication. Transformative relationships built on intellectual connection and trust.",
                },
                {
                  question: "3. What does 2026 look like for Aquarius career growth?",
                  answer: "Innovation drives professional success. Leadership opportunities through strategic thinking and creativity.",
                },
                {
                  question: "4. What health advice for Aquarius in 2026?",
                  answer: "Holistic approach maintains energy. Regular exercise, meditation, and self-care prevent burnout.",
                },
                {
                  question: "5. Key astrological remedies for Aquarius?",
                  answer: "Amethyst/Blue Sapphire, structured routine, and Thursday charity enhance clarity and growth.",
                },
              ].map((item, index) => (
                <div className="faq-item" key={index}>
                  <div
                    className={`faq-question ${
                      activeIndex === index ? "active" : ""
                    }`}
                    onClick={() => toggleAnswer(index)}
                  >
                    {item.question}
                  </div>
                  <div
                    className={`faq-answer ${
                      activeIndex === index ? "show" : ""
                    }`}
                  >
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AquariusCard;
